import type { CardAttachment, CardType, MemoryCard } from "./types";

export interface StructuredField {
  key: string;
  label: string;
  value: string;
}

export interface MetadataFieldDefinition {
  key: string;
  label: string;
  multiline?: boolean;
  list?: boolean;
}

const fieldDefinitions: Partial<Record<CardType, MetadataFieldDefinition[]>> = {
  movie: [
    { key: "director", label: "Режиссёр" },
    { key: "year", label: "Год" },
    { key: "genre", label: "Жанр" },
    { key: "watchStatus", label: "Статус" },
  ],
  recipe: [
    { key: "calories", label: "Калорийность" },
    { key: "servings", label: "Порции" },
    { key: "ingredients", label: "Ингредиенты", multiline: true, list: true },
    { key: "steps", label: "Приготовление", multiline: true, list: true },
  ],
  place: [
    { key: "address", label: "Адрес" },
    { key: "coordinates", label: "Координаты" },
    { key: "openingHours", label: "Часы работы" },
    { key: "phones", label: "Телефон", list: true },
  ],
  link: [
    { key: "site", label: "Сайт" },
    { key: "price", label: "Цена" },
  ],
  task: [
    { key: "due", label: "Срок" },
    { key: "repeat", label: "Повторение" },
    { key: "taskStatus", label: "Статус" },
  ],
  material: [
    { key: "author", label: "Автор" },
    { key: "format", label: "Формат" },
    { key: "readingStatus", label: "Статус" },
  ],
  video: [
    { key: "channel", label: "Канал" },
    { key: "durationSeconds", label: "Длительность" },
    { key: "watchStatus", label: "Статус просмотра" },
    { key: "originalLanguage", label: "Язык оригинала" },
    { key: "transcriptStatus", label: "Транскрипт" },
    { key: "keyPoints", label: "Ключевые мысли", multiline: true, list: true },
  ],
  idea: [{ key: "nextStep", label: "Следующий шаг" }],
};

const aliases: Record<string, string[]> = {
  director: ["director", "режиссер", "режиссёр"],
  year: ["year", "releaseYear"],
  genre: ["genre", "genres"],
  watchStatus: ["watchStatus", "status"],
  calories: ["calories", "caloriesPer100g", "калорийность"],
  servings: ["servings", "portions"],
  ingredients: ["ingredients"],
  steps: ["steps", "instructions"],
  address: ["address"],
  coordinates: ["coordinates"],
  openingHours: ["openingHours", "hours"],
  phones: ["phones", "phone"],
  site: ["site", "domain"],
  price: ["price"],
  due: ["due", "dueAt"],
  repeat: ["repeat", "rrule"],
  taskStatus: ["taskStatus", "status"],
  author: ["author"],
  format: ["format"],
  readingStatus: ["readingStatus", "status"],
  nextStep: ["nextStep"],
  channel: ["channel"],
  durationSeconds: ["durationSeconds"],
  originalLanguage: ["originalLanguage"],
  transcriptStatus: ["transcriptStatus"],
  keyPoints: ["keyPoints"],
};

function printable(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (Array.isArray(value)) return value.map(printable).filter(Boolean).join("\n");
  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => printable(item) ? key + ": " + printable(item) : "")
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

function metadataValue(card: MemoryCard, key: string): string {
  if (key === "durationSeconds") {
    const seconds = Number(card.metadata.durationSeconds ?? 0);
    if (!Number.isFinite(seconds) || seconds <= 0) return "";
    const minutes = Math.floor(seconds / 60);
    const remainder = Math.round(seconds % 60);
    return remainder ? `${minutes} мин ${remainder} сек` : `${minutes} мин`;
  }
  if (key === "watchStatus") {
    return card.metadata.watchStatus === "watched" ? "Просмотрено" : "Посмотреть";
  }
  if (key === "transcriptStatus") {
    const status = String(card.metadata.transcriptStatus ?? "");
    return ({
      pending: "Обрабатывается",
      native: "Готовые субтитры",
      generated: "Распознано автоматически",
      approval_required: "Нужно подтверждение",
      failed: "Ошибка обработки",
    } as Record<string, string>)[status] ?? status;
  }
  if (key === "coordinates") {
    const direct = printable(card.metadata.coordinates);
    if (direct) return direct;
    const latitude = printable(card.metadata.latitude);
    const longitude = printable(card.metadata.longitude);
    if (latitude && longitude) return latitude + ", " + longitude;
  }
  for (const alias of aliases[key] ?? [key]) {
    const value = printable(card.metadata[alias]);
    if (value) return value;
  }
  if (key === "director" || key === "author") {
    const entity = card.entities.find((item) => item.kind.toLowerCase().includes(key));
    if (entity) return entity.name;
  }
  if (key === "site" && card.source_url) {
    try {
      return new URL(card.source_url).hostname.replace(/^www\./, "");
    } catch {
      return "";
    }
  }
  if (key === "due" && card.event_start) return new Date(card.event_start).toLocaleString("ru-RU");
  if (key === "calories") {
    const match = (card.summary + "\n" + card.original_text).match(/(\d+(?:[.,]\d+)?)\s*ккал(?:\s*(?:на|\/)\s*\d+\s*г)?/i);
    return match?.[0] ?? "";
  }
  return "";
}

export function metadataFieldDefinitions(type: CardType): MetadataFieldDefinition[] {
  return fieldDefinitions[type] ?? [];
}

export function structuredFields(card: MemoryCard): StructuredField[] {
  return metadataFieldDefinitions(card.type)
    .map((definition) => ({ ...definition, value: metadataValue(card, definition.key) }))
    .filter((field) => Boolean(field.value));
}

export function cardSources(card: MemoryCard): Array<{ title: string; url: string }> {
  const sources: Array<{ title: string; url: string }> = [];
  if (card.source_url) sources.push({ title: "Исходная ссылка", url: card.source_url });
  const webSources = card.metadata.webSources;
  if (Array.isArray(webSources)) {
    for (const item of webSources) {
      if (!item || typeof item !== "object") continue;
      const title = "title" in item && typeof item.title === "string" ? item.title : "Источник";
      const url = "url" in item && typeof item.url === "string" ? item.url : "";
      if (/^https?:\/\//i.test(url)) sources.push({ title, url });
    }
  }
  return [...new Map(sources.map((source) => [source.url, source])).values()];
}

export function cardImageAttachment(card: MemoryCard): CardAttachment | null {
  return card.attachments.find((item) => item.role === "cover" && item.mime_type.startsWith("image/"))
    ?? card.attachments.find((item) => item.mime_type.startsWith("image/"))
    ?? null;
}

export function filterCards(cards: MemoryCard[], query: string, tag: string): MemoryCard[] {
  const normalizedQuery = query.trim().toLocaleLowerCase("ru-RU");
  const normalizedTag = tag.trim().toLocaleLowerCase("ru-RU");
  return cards.filter((card) => {
    const matchesTag = !normalizedTag || card.tags.some((item) => item.toLocaleLowerCase("ru-RU") === normalizedTag);
    const haystack = [card.title, card.summary, card.original_text, ...card.tags].join(" ").toLocaleLowerCase("ru-RU");
    return matchesTag && (!normalizedQuery || haystack.includes(normalizedQuery));
  });
}

export function popularTags(cards: MemoryCard[], limit = 12): Array<{ name: string; count: number }> {
  const counts = new Map<string, { name: string; count: number }>();
  for (const card of cards) {
    for (const tag of card.tags) {
      const key = tag.toLocaleLowerCase("ru-RU");
      const current = counts.get(key);
      counts.set(key, { name: current?.name ?? tag, count: (current?.count ?? 0) + 1 });
    }
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "ru")).slice(0, limit);
}
